import { useState, useEffect } from "react";
import { WaterEntry } from "@/types/water";
import { useLanguage } from "@/contexts/LanguageContext";

interface UseWaterFormArgs {
  formEntry: WaterEntry;
  editing: boolean;
  onSave: () => void;
  t: (key: string) => string;
}

export const useWaterForm = ({ formEntry, editing, onSave, t }: UseWaterFormArgs) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState("sources");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!editing) {
      setTouched({});
      setErrors({});
      setActiveTab("sources");
    }
  }, [editing]);

  // Validation function with custom touched state
  const validateFormWithTouched = (touchedState: Record<string, boolean>): boolean => {
    const newErrors: Record<string, string> = {};

    // Water surface area validation
    if (touchedState.waterSurfaceArea) {
      const surfaceOpenWaterSource = t("water.surfaceWaterSource").toLowerCase();
      const usesSurfaceWater = formEntry.waterOrigins.some(
        (origin) => origin.sourceName?.trim().toLowerCase() === surfaceOpenWaterSource
      );

      if (
        formEntry.waterSurfaceArea === "" ||
        (usesSurfaceWater && Number(formEntry.waterSurfaceArea) < 0) ||
        (!usesSurfaceWater && Number(formEntry.waterSurfaceArea) < 0)
      ) {
        newErrors.waterSurfaceArea = usesSurfaceWater
          ? t("water.openWaterSurfaceAreaRequired")
          : t("water.waterSurfaceAreaRequired");
      }
    }

    // Water origins validation
    if (touchedState.waterOrigins && formEntry.waterOrigins.length === 0) {
      newErrors.waterOrigins = t("water.waterOriginsRequired");
    }

    formEntry.waterOrigins.forEach((origin, index) => {
      const sourceKey = `origin_source_${index}`;
      const quantityKey = `origin_quantity_${index}`;

      if (touchedState[sourceKey] && !origin.sourceId) {
        newErrors[sourceKey] = t("water.waterSourceRequired");
      }
      // Check if this is a surface water source
      const surfaceOpenWaterSource = t("water.surfaceWaterSource").toLowerCase();
      const isSurfaceWater = origin.sourceName?.trim().toLowerCase() === surfaceOpenWaterSource;
      
      if (touchedState[quantityKey]) {
        if (isSurfaceWater) {
          // Surface water can accept 0
          if (origin.quantity === "" || Number(origin.quantity) < 0) {
            newErrors[quantityKey] = t("water.quantityRequired");
          }
        } else {
          // Non-surface water requires values > 0
          if (!origin.quantity || Number(origin.quantity) <= 0) {
            newErrors[quantityKey] = t("water.quantityRequired");
          }
        }
      }
    });

    // Discharge validation
    if (touchedState.hasDischarge && formEntry.discharge.hasDischarge === null) {
      newErrors.hasDischarge = t("water.dischargeDecisionRequired");
    }

    // Sludge validation
    if (touchedState.hasSludge && formEntry.sludge.hasSludge === null) {
      newErrors.hasSludge = t("water.sludgeDecisionRequired");
    }

    if (formEntry.discharge.hasDischarge) {
      if (touchedState.dischargeEntries && formEntry.discharge.dischargeEntries.length === 0) {
        newErrors.dischargeEntries = t("water.dischargeTypesRequired");
      }

      formEntry.discharge.dischargeEntries.forEach((discharge, index) => {
        const typeKey = `discharge_type_${index}`;
        const quantityKey = `discharge_quantity_${index}`;

        if (touchedState[typeKey] && !discharge.dischargeTypeId) {
          newErrors[typeKey] = t("water.dischargeTypeRequired");
        }
        if (touchedState[quantityKey] && (!discharge.quantity || Number(discharge.quantity) <= 0)) {
          newErrors[quantityKey] = t("water.dischargeQuantityRequired");
        }
      });

      // Validate pollutants - at least one is required
      if (touchedState.pollutants && formEntry.discharge.pollutants.length === 0) {
        newErrors.pollutants = t("water.pollutantsRequired");
      }

      formEntry.discharge.pollutants.forEach((pollutant, index) => {
        const pollutantKey = `pollutant_${index}`;
        const concentrationKey = `concentration_${index}`;
        const monitoringKey = `monitoring_${index}`;
        const receiverKey = `receiverConcentration_${index}`;

        if (touchedState[pollutantKey] && !pollutant.pollutantId) {
          newErrors[pollutantKey] = t("water.pollutantTypeRequired");
        }

        if (touchedState[concentrationKey] && (!pollutant.concentration || Number(pollutant.concentration) <= 0)) {
          newErrors[concentrationKey] = t("water.concentrationRequired");
        }

        if (pollutant.pollutantId && touchedState[monitoringKey] && pollutant.hasMonitoring === null) {
          newErrors[monitoringKey] = t("water.monitoringDecisionRequired");
        }

        if (
          pollutant.hasMonitoring === true &&
          touchedState[receiverKey] &&
          (!pollutant.concentrationInReceiver || Number(pollutant.concentrationInReceiver) < 0)
        ) {
          newErrors[receiverKey] = t("water.receiverConcentrationRequired");
        }
      });
    }

    // Sludge validation
    if (formEntry.sludge.hasSludge) {
      if (
        touchedState.sludgeQuantity &&
        (!formEntry.sludge.sludgeQuantity || Number(formEntry.sludge.sludgeQuantity) <= 0)
      ) {
        newErrors.sludgeQuantity = t("water.sludgeQuantityRequired");
      }
      if (touchedState.sludgeUnit && !formEntry.sludge.sludgeUnit) {
        newErrors.sludgeUnit = t("water.sludgeUnitRequired");
      }

      // Moisture content validation
      if (
        touchedState.moistureContent &&
        (!formEntry.sludge.moistureContent ||
          Number(formEntry.sludge.moistureContent) < 0 ||
          Number(formEntry.sludge.moistureContent) > 100)
      ) {
        newErrors.moistureContent = t("water.moistureContentRequired");
      }

      // Deposition decision validation
      if (touchedState.isDepositedInBasin && formEntry.sludge.isDepositedInBasin === null) {
        newErrors.isDepositedInBasin = t("water.sludgeDepositionRequired");
      }

      // If deposited in basin, validate sludge pollutants
      if (formEntry.sludge.isDepositedInBasin) {
        if (
          touchedState.sludgePollutants &&
          (!formEntry.sludge.pollutants || formEntry.sludge.pollutants.length === 0)
        ) {
          newErrors.sludgePollutants = t("water.pollutantsRequired");
        }

        (formEntry.sludge.pollutants || []).forEach((pollutant, index) => {
          const pollutantKey = `sludgePollutant_${index}`;
          const concentrationKey = `sludgeConcentration_${index}`;
          const monitoringKey = `sludgeMonitoring_${index}`;
          const receiverKey = `sludgeReceiverConcentration_${index}`;

          if (touchedState[pollutantKey] && !pollutant.pollutantId) {
            newErrors[pollutantKey] = t("water.pollutantTypeRequired");
          }

          if (touchedState[concentrationKey] && (!pollutant.concentration || Number(pollutant.concentration) <= 0)) {
            newErrors[concentrationKey] = t("water.concentrationRequired");
          }

          if (pollutant.pollutantId && touchedState[monitoringKey] && pollutant.hasMonitoring === null) {
            newErrors[monitoringKey] = t("water.monitoringDecisionRequired");
          }

          if (
            pollutant.hasMonitoring === true &&
            touchedState[receiverKey] &&
            (!pollutant.concentrationInReceiver || Number(pollutant.concentrationInReceiver) < 0)
          ) {
            newErrors[receiverKey] = t("water.receiverConcentrationRequired");
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validation function
  const validateForm = (): boolean => {
    return validateFormWithTouched(touched);
  };

  // Tab completion status
  const getTabStatus = (tabId: string) => {
    switch (tabId) {
      case "sources":
        const hasSurfaceArea =
          formEntry.waterSurfaceArea !== "" &&
          Number(formEntry.waterSurfaceArea) >= 0;
        const hasValidOrigins =
          formEntry.waterOrigins.length > 0 &&
          formEntry.waterOrigins.every((origin) => {
            if (!origin.sourceId || !origin.quantity) return false;
            
            // Check if this is a surface water source
            const surfaceOpenWaterSource = t("water.surfaceWaterSource").toLowerCase();
            const isSurfaceWater = origin.sourceName?.trim().toLowerCase() === surfaceOpenWaterSource;
            
            if (isSurfaceWater) {
              // Surface water can accept 0
              return Number(origin.quantity) >= 0;
            } else {
              // Non-surface water requires values > 0
              return Number(origin.quantity) > 0;
            }
          });
        const hasDischargeDecision = formEntry.discharge.hasDischarge !== null;
        const hasSludgeDecision = formEntry.sludge.hasSludge !== null;
        return hasSurfaceArea && hasValidOrigins && hasDischargeDecision && hasSludgeDecision;
      case "discharge":
        // Require a decision to be made first
        if (formEntry.discharge.hasDischarge === null) return false;
        // If no discharge, tab is complete
        if (!formEntry.discharge.hasDischarge) return true;
        // If has discharge, validate required fields
        const hasValidDischargeEntries =
          formEntry.discharge.dischargeEntries.length > 0 &&
          formEntry.discharge.dischargeEntries.every(
            (discharge) => discharge.dischargeTypeId && discharge.quantity && Number(discharge.quantity) > 0
          );
        // Require at least one pollutant with valid data
        const hasValidPollutants =
          formEntry.discharge.pollutants.length > 0 &&
          formEntry.discharge.pollutants.every((pollutant) => {
            const hasBasicData =
              pollutant.pollutantId && pollutant.concentration && Number(pollutant.concentration) > 0;
            const hasMonitoringDecision = pollutant.hasMonitoring !== null;
            const hasValidReceiverConcentration =
              pollutant.hasMonitoring !== true ||
              (pollutant.concentrationInReceiver && Number(pollutant.concentrationInReceiver) >= 0);
            return hasBasicData && hasMonitoringDecision && hasValidReceiverConcentration;
          });
        return hasValidDischargeEntries && hasValidPollutants;
      case "sludge":
        if (!formEntry.sludge.hasSludge) return true;

        // Basic sludge fields validation
        const hasBasicSludgeData =
          formEntry.sludge.sludgeQuantity &&
          Number(formEntry.sludge.sludgeQuantity) > 0 &&
          formEntry.sludge.sludgeUnit &&
          formEntry.sludge.moistureContent &&
          Number(formEntry.sludge.moistureContent) >= 0 &&
          Number(formEntry.sludge.moistureContent) <= 100 &&
          formEntry.sludge.isDepositedInBasin !== null;

        if (!hasBasicSludgeData) return false;

        // If not deposited in basin, tab is complete
        if (!formEntry.sludge.isDepositedInBasin) return true;

        // If deposited in basin, validate pollutants
        const hasValidSludgePollutants =
          formEntry.sludge.pollutants &&
          formEntry.sludge.pollutants.length > 0 &&
          formEntry.sludge.pollutants.every((pollutant) => {
            const hasBasicData =
              pollutant.pollutantId && pollutant.concentration && Number(pollutant.concentration) > 0;
            const hasMonitoringDecision = pollutant.hasMonitoring !== null;
            const hasValidReceiverConcentration =
              pollutant.hasMonitoring !== true ||
              (pollutant.concentrationInReceiver && Number(pollutant.concentrationInReceiver) >= 0);
            return hasBasicData && hasMonitoringDecision && hasValidReceiverConcentration;
          });

        return hasValidSludgePollutants;
      default:
        return false;
    }
  };

  // Check if form is valid for enabling/disabling save button
  const isFormValid = (): boolean => {
    const { t } = useLanguage();
    if (
      formEntry.waterSurfaceArea === "" ||
      Number(formEntry.waterSurfaceArea) < 0
    ) {
      return false;
    }

    // Check water origins
    if (formEntry.waterOrigins.length === 0) return false;
    if (!formEntry.waterOrigins.every((origin) => {
      if (!origin.sourceId || !origin.quantity) return false;
      
      // Check if this is a surface water source
      const surfaceOpenWaterSource = t("water.surfaceWaterSource");
      const isSurfaceWater = origin.sourceName === surfaceOpenWaterSource;
      
      if (isSurfaceWater) {
        // Surface water can accept 0
        return Number(origin.quantity) >= 0;
      } else {
        // Non-surface water requires values > 0
        return Number(origin.quantity) > 0;
      }
    }))
      return false;

    // Require discharge decision to be made
    if (formEntry.discharge.hasDischarge === null) return false;

    // Require sludge decision to be made
    if (formEntry.sludge.hasSludge === null) return false;

    // Check discharge if enabled
    if (formEntry.discharge.hasDischarge) {
      if (formEntry.discharge.dischargeEntries.length === 0) return false;
      if (
        !formEntry.discharge.dischargeEntries.every(
          (discharge) => discharge.dischargeTypeId && discharge.quantity && Number(discharge.quantity) > 0
        )
      )
        return false;

      // Require at least one pollutant
      if (formEntry.discharge.pollutants.length === 0) return false;

      // Check pollutants validation
      if (
        !formEntry.discharge.pollutants.every((pollutant) => {
          if (!pollutant.pollutantId || !pollutant.concentration || Number(pollutant.concentration) <= 0) return false;
          // Monitoring decision is required
          if (pollutant.hasMonitoring === null) return false;
          // If monitoring is enabled, receiver concentration is required
          if (
            pollutant.hasMonitoring === true &&
            (!pollutant.concentrationInReceiver || Number(pollutant.concentrationInReceiver) < 0)
          )
            return false;
          return true;
        })
      )
        return false;
    }

    // Check sludge if enabled
    if (formEntry.sludge.hasSludge) {
      if (
        !formEntry.sludge.sludgeQuantity ||
        Number(formEntry.sludge.sludgeQuantity) <= 0 ||
        !formEntry.sludge.sludgeUnit ||
        !formEntry.sludge.moistureContent ||
        Number(formEntry.sludge.moistureContent) < 0 ||
        Number(formEntry.sludge.moistureContent) > 100 ||
        formEntry.sludge.isDepositedInBasin === null
      )
        return false;

      // If deposited in basin, validate sludge pollutants
      if (formEntry.sludge.isDepositedInBasin) {
        if (!formEntry.sludge.pollutants || formEntry.sludge.pollutants.length === 0) return false;

        if (
          !formEntry.sludge.pollutants.every((pollutant) => {
            if (!pollutant.pollutantId || !pollutant.concentration || Number(pollutant.concentration) <= 0)
              return false;
            // Monitoring decision is required
            if (pollutant.hasMonitoring === null) return false;
            // If monitoring is enabled, receiver concentration is required
            if (
              pollutant.hasMonitoring === true &&
              (!pollutant.concentrationInReceiver || Number(pollutant.concentrationInReceiver) < 0)
            )
              return false;
            return true;
          })
        )
          return false;
      }
    }

    return true;
  };

  // Handle save with validation
  const handleSaveClick = () => {
    const newTouched: Record<string, boolean> = {
      waterSurfaceArea: true,
      waterOrigins: true,
      hasDischarge: true,
      hasSludge: true,
    };

    // Mark all origin quantities as touched
    formEntry.waterOrigins.forEach((_origin, index) => {
      newTouched[`origin_quantity_${index}`] = true;
    });

    if (formEntry.discharge.hasDischarge) {
      newTouched.dischargeEntries = true;
      newTouched.pollutants = true;

      formEntry.discharge.dischargeEntries.forEach((_, index) => {
        newTouched[`discharge_quantity_${index}`] = true;
      });

      formEntry.discharge.pollutants.forEach((_, index) => {
        newTouched[`pollutant_${index}`] = true;
        newTouched[`concentration_${index}`] = true;
        newTouched[`monitoring_${index}`] = true;
        newTouched[`receiverConcentration_${index}`] = true;
      });
    }

    if (formEntry.sludge.hasSludge) {
      newTouched.sludgeQuantity = true;
      newTouched.sludgeUnit = true;
      newTouched.moistureContent = true;
      newTouched.isDepositedInBasin = true;

      if (formEntry.sludge.isDepositedInBasin) {
        newTouched.sludgePollutants = true;
        (formEntry.sludge.pollutants || []).forEach((_, index) => {
          newTouched[`sludgePollutant_${index}`] = true;
          newTouched[`sludgeConcentration_${index}`] = true;
          newTouched[`sludgeMonitoring_${index}`] = true;
          newTouched[`sludgeReceiverConcentration_${index}`] = true;
        });
      }
    }

    setTouched(newTouched);

    if (validateForm()) {
      onSave();
    }
  };

  // Handle field changes without touch tracking
  const handleFieldChange = (field: string, value: any) => {
    // Clear specific error when field is being corrected
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Handle tab switching when discharge/sludge decisions change
    if (field === "hasDischarge" && value === false && activeTab === "discharge") {
      setActiveTab("sources");
    }
    if (field === "hasSludge" && value === false && activeTab === "sludge") {
      setActiveTab("sources");
    }
  };

  // Handle field blur
  const handleFieldBlur = (field: string) => {
    setTouched((prev) => {
      const newTouched = { ...prev, [field]: true };
      setTimeout(() => {
        validateFormWithTouched(newTouched);
      }, 0);
      return newTouched;
    });
  };

  // Reset form state (when modal is closed)
  const resetForm = () => {
    setActiveTab("sources");
    setErrors({});
    setTouched({});
  };

  return {
    errors,
    activeTab,
    touched,
    setActiveTab,
    validateForm,
    getTabStatus,
    isFormValid,
    handleSaveClick,
    handleFieldChange,
    handleFieldBlur,
    resetForm,
  };
};
